import React, {FC, memo, useState} from "react";
import {Button, Modal, Steps, Radio, Checkbox, Alert, Input, Result, Spin, message} from "antd";
import {blue, green} from "@ant-design/colors";
import MoneyInput from "../../../../views/MoneyInput/MoneyInput";
import i18n from "../../../../services/i18n";
import {useCurrency} from "../../../../helpers/currencyHelper";
import {renderAmount, CurrencyShort} from "../../../../helpers/renderAmount";
import {buildCorrectionSentences} from "./correctionSentences";
import ActiveDunningAlert from "../BookingDunnings/ActiveDunningAlert";
import {
    Booking,
    CorrectionRefundMode,
    PaymentType,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetInvoiceBodyByIdDocument,
    usePreviewSettledBookingCorrectionLazyQuery,
    useExecuteSettledBookingCorrectionMutation,
    useGetBookingCorrectionContextLazyQuery,
} from "../../../../generated/graphql";

/** Maximale Länge der Begründung — der Server kürzt auf denselben Wert. */
const REASON_MAX_LENGTH = 200;

type CorrectBookingWizardProps = {
    bookingId: Booking["id"];
    invoiceId?: string;
    /** Aktueller booking.grossPrice — Optimistic-Concurrency-Precondition. */
    expectedGrossPrice: number;
    /** Aktueller Basispreis (grossPrice + Rabatte + Guthaben) — Default für das Preisfeld. */
    currentBasePrice: number;
    /** Bisher verrechnetes Guthaben (Default für das Guthaben-Feld). */
    currentCreditedBalance: number;
    /** Aktuell verfügbares Bucher-Guthaben. */
    availableCredit: number;
    /**
     * Wechsel der Zahlweise (Einmalzahlung ↔ Monatlich, #912). Weggelassen = Auswahl ausgeblendet
     * (z. B. Einzelbuchungen). Die Kurs-Erlaubnis deaktiviert nur die Option; der Server prüft verbindlich.
     */
    paymentTypeSwitch?: {current: PaymentType; allowMonthly: boolean; allowUpFront: boolean};
};

type ExecResult = {refundOk: boolean; paymentLinkOk: boolean; correctionInvoiceOk: boolean; messages: Array<string>};

/**
 * Formatiert einen Betrag in der übergebenen Tenant-Währung (z.B. "€"/"CHF").
 * Pure Helper — testbar ohne React/Apollo. Innerhalb der Komponente wird die
 * aktive Währung über useCurrency() gebunden (siehe lokales renderMoney).
 */
export const formatMoney = (value: number | null | undefined, currency: CurrencyShort): string =>
    renderAmount({amount: Number(value ?? 0), currency, short: true});

/**
 * Rundet auf Cent und hält unvollständige Daten aus: die Preis-Props kommen direkt aus der
 * Buchungs-Query und können zur Laufzeit fehlen, obwohl sie als `number` typisiert sind.
 * Ohne diesen Schutz riss ein fehlender grossPrice die ganze Abrechnungsseite mit.
 */
const round2 = (value: number | null | undefined): number => {
    const parsed = Number(value ?? 0);

    return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
};

/** Zelle für Geldbeträge: rechtsbündig und mit Tabellenziffern, damit die Spalten fluchten. */
const amountCellStyle: React.CSSProperties = {textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", padding: "6px 0"};
const labelCellStyle: React.CSSProperties = {padding: "6px 0"};
const arrowCellStyle: React.CSSProperties = {textAlign: "center", width: 32, color: "rgba(0, 0, 0, 0.45)"};

const CorrectBookingWizard: FC<CorrectBookingWizardProps> = ({
    bookingId,
    invoiceId,
    expectedGrossPrice,
    currentBasePrice,
    currentCreditedBalance,
    availableCredit,
    paymentTypeSwitch,
}) => {
    const tr = i18n.containers.invoice.CorrectBookingWizard;
    const currency = useCurrency();
    const renderMoney = (value: number | null | undefined): string => formatMoney(value, currency.short as CurrencyShort);

    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [newBasePrice, setNewBasePrice] = useState<number>(currentBasePrice);
    const [creditedBalance, setCreditedBalance] = useState<number>(currentCreditedBalance);
    const [refundMode, setRefundMode] = useState<CorrectionRefundMode | undefined>(undefined);
    const [newPaymentType, setNewPaymentType] = useState<PaymentType | undefined>(paymentTypeSwitch?.current);
    const [reason, setReason] = useState("");
    const [sendMail, setSendMail] = useState(false);
    const [execResult, setExecResult] = useState<ExecResult | undefined>(undefined);
    // Preis vor der Ausführung. `expectedGrossPrice` trägt nach dem Refetch bereits den neuen
    // Wert — ohne diesen Merker fiele der Vorher-Nachher-Satz im Ergebnis-Schritt weg.
    const [grossPriceBeforeExec, setGrossPriceBeforeExec] = useState<number | undefined>(undefined);

    const maxCredit = round2(availableCredit + currentCreditedBalance);
    const creditIncreased = creditedBalance > currentCreditedBalance;

    // newPaymentType nur senden, wenn tatsächlich gewechselt wird — sonst bleibt das Verhalten
    // (und der Request) exakt wie vor #912.
    const paymentTypeChanged = Boolean(paymentTypeSwitch && newPaymentType && newPaymentType !== paymentTypeSwitch.current);
    const input = {
        newBasePrice,
        creditedBalance,
        refundMode,
        expectedGrossPrice,
        ...(paymentTypeChanged ? {newPaymentType} : {}),
        ...(reason.trim() ? {reason: reason.trim()} : {}),
    };

    // Rabatte und Raten-Fälligkeiten für Rechnung und Sätze. Der Wizard holt sie selbst, damit
    // er nur `bookingId` braucht und beide Detail-Seiten unverändert bleiben.
    const [loadContext, {data: contextData}] = useGetBookingCorrectionContextLazyQuery({fetchPolicy: "no-cache"});
    const bookingContext = contextData?.bookingById;
    const discount = Number(bookingContext?.discount ?? 0);
    const discountName = bookingContext?.discountName;
    const memberDiscount = Number(bookingContext?.memberDiscount ?? 0);
    const rates = bookingContext?.scheduledPayments ?? [];

    // Zwei Abfragen derselben Query: die Baseline liefert beim Öffnen `alreadyPaid`, damit
    // Schritt 1 live rechnen kann. Ihre `blockers` sind bewusst ungenutzt — bei unveränderten
    // Eingaben meldet der Planer dort immer „Keine Änderung erkannt“ (No-op-Guard). Die echten
    // Blocker zeigt die zweite Abfrage, die eigentliche Vorschau in Schritt 2.
    const [loadBaseline, {data: baselineData, loading: baselineLoading, error: baselineError}] =
        usePreviewSettledBookingCorrectionLazyQuery({fetchPolicy: "no-cache"});
    const baseline = baselineData?.previewSettledBookingCorrection;

    const [loadPreview, {data: previewData, loading: previewLoading, error: previewError}] = usePreviewSettledBookingCorrectionLazyQuery({
        fetchPolicy: "no-cache",
    });
    const preview = previewData?.previewSettledBookingCorrection;

    const [execute, {loading: executing}] = useExecuteSettledBookingCorrectionMutation({
        refetchQueries: [
            {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: bookingId}},
            ...(invoiceId ? [{query: GetInvoiceBodyByIdDocument, variables: {id: invoiceId}}] : []),
        ],
    });

    const close = () => {
        setOpen(false);
        setStep(0);
        setNewBasePrice(currentBasePrice);
        setCreditedBalance(currentCreditedBalance);
        setRefundMode(undefined);
        setNewPaymentType(paymentTypeSwitch?.current);
        setReason("");
        setSendMail(false);
        setExecResult(undefined);
        setGrossPriceBeforeExec(undefined);
    };

    // Modal ist dauerhaft gemountet: States beim Öffnen frisch aus den aktuellen Props initialisieren,
    // damit zwischenzeitliche Buchungsänderungen (Refetch) nicht mit veralteten useState-Werten überschrieben werden.
    const openWizard = () => {
        setStep(0);
        setNewBasePrice(currentBasePrice);
        setCreditedBalance(currentCreditedBalance);
        setRefundMode(undefined);
        setNewPaymentType(paymentTypeSwitch?.current);
        setReason("");
        setSendMail(false);
        setExecResult(undefined);
        setGrossPriceBeforeExec(undefined);
        setOpen(true);
        loadContext({variables: {id: bookingId}});
        loadBaseline({
            variables: {bookingId, input: {newBasePrice: currentBasePrice, creditedBalance: currentCreditedBalance, expectedGrossPrice}},
        });
    };

    const goToPreview = () => {
        loadPreview({variables: {bookingId, input}});
        setStep(1);
    };

    const runExecute = async () => {
        setGrossPriceBeforeExec(round2(expectedGrossPrice));
        try {
            const res = await execute({variables: {bookingId, input, sendCorrectionInvoiceMail: sendMail}});
            const result = res.data?.executeSettledBookingCorrection;

            if (result) {
                setExecResult(result);
                if (!result.refundOk || !result.paymentLinkOk || !result.correctionInvoiceOk) {
                    message.warning(tr.followupFailed());
                }
            }
            setStep(2);
        } catch (error) {
            message.error(error instanceof Error ? error.message : tr.executeFailed());
        }
    };

    // ── Live-Rechnung in Schritt 1 ──────────────────────────────────────
    // Der Server rechnet `newTotalDue = Basispreis − Rabatt − Mitglieder-Rabatt − Guthaben`.
    // Hier steht die algebraisch gleiche, aber abzugsfreie Form: weil
    // `currentBasePrice = grossPrice + Rabatte + Guthaben` gilt, kürzen sich die Abzüge gegen
    // die Differenz zum aktuellen Basispreis weg. Damit hängt die Rechnung NICHT an der
    // Kontext-Query — käme die langsamer oder gar nicht, wären `discount`/`memberDiscount` 0
    // und die Anzeige widerspräche dem Server (Review #1287). Der Kontext liefert nur noch
    // die Rabatt-Zeilen und die Raten-Daten, also reine Darstellung.
    // `alreadyPaid` hängt nicht vom Eingabewert ab und stammt unverändert aus der Baseline.
    const alreadyPaid = Number(baseline?.alreadyPaid ?? 0);
    const liveGross = round2(expectedGrossPrice + (newBasePrice - currentBasePrice) - (creditedBalance - currentCreditedBalance));
    const liveOpen = Math.max(0, round2(liveGross - alreadyPaid));
    const liveOverpayment = Math.max(0, round2(alreadyPaid - liveGross));
    const currentOpen = Math.max(0, round2(expectedGrossPrice - alreadyPaid));
    const priceUnchanged = liveGross === round2(expectedGrossPrice);
    // Sperre nur bei echtem No-op. Der Wechsel der Zahlweise (#912) und eine geänderte
    // Guthaben-Verrechnung sind für sich genommen gültige Korrekturen, auch ohne neuen Preis.
    const nothingChanged = priceUnchanged && !paymentTypeChanged && round2(creditedBalance) === round2(currentCreditedBalance);

    const blockersPresent = (preview?.blockers.length ?? 0) > 0;
    // Überzahlung braucht eine Entscheidung — sonst blockiert der Server die Vorschau.
    const refundModeMissing = liveOverpayment > 0 && refundMode === undefined;

    // Der Wechsel steht nur zur Wahl, wenn die Gegenrichtung überhaupt erlaubt ist; sonst
    // bleibt nur der Hinweis, dass die Zahlweise unverändert bleibt.
    const canSwitchPaymentType = Boolean(
        paymentTypeSwitch
        && ((paymentTypeSwitch.current === PaymentType.PrePaid && paymentTypeSwitch.allowMonthly)
            || (paymentTypeSwitch.current === PaymentType.Monthly && paymentTypeSwitch.allowUpFront)),
    );
    const currentPaymentTypeLabel =
        paymentTypeSwitch?.current === PaymentType.Monthly ? tr.paymentTypes.Monthly() : tr.paymentTypes.PrePaid();

    const renderLedgerRow = (label: React.ReactNode, oldValue: React.ReactNode, newValue: React.ReactNode, options?: {sum?: boolean; muted?: boolean}) => (
        <tr>
            <td style={{...labelCellStyle, ...(options?.sum ? {borderTop: "1px solid rgba(0,0,0,.85)", fontWeight: 600, paddingTop: 8} : {}), ...(options?.muted ? {color: "rgba(0,0,0,.45)"} : {})}}>
                {label}
            </td>
            <td style={{...amountCellStyle, ...(options?.sum ? {borderTop: "1px solid rgba(0,0,0,.85)", fontWeight: 600, paddingTop: 8} : {}), ...(options?.muted ? {color: "rgba(0,0,0,.45)"} : {})}}>
                {oldValue}
            </td>
            <td style={{...arrowCellStyle, ...(options?.sum ? {borderTop: "1px solid rgba(0,0,0,.85)", paddingTop: 8} : {})}}>
                {oldValue !== newValue ? "→" : ""}
            </td>
            <td style={{...amountCellStyle, ...(options?.sum ? {borderTop: "1px solid rgba(0,0,0,.85)", fontWeight: 600, paddingTop: 8} : {}), ...(options?.muted ? {color: "rgba(0,0,0,.45)"} : {})}}>
                {newValue}
            </td>
        </tr>
    );

    const renderOpenAmountTag = () => {
        if (liveOverpayment > 0) {
            const tagText = tr.ledger.overpaid({amount: renderMoney(liveOverpayment)});

            return <span style={{marginLeft: 8, fontSize: 12, color: "#d46b08"}}>{tagText}</span>;
        }
        const tagText = liveOpen === 0 ? tr.ledger.nothingOpen() : tr.ledger.staysOpen();

        return <span style={{marginLeft: 8, fontSize: 12, color: liveOpen === 0 ? green[7] : "rgba(0,0,0,.45)"}}>{tagText}</span>;
    };

    const renderInputStep = () => {
        if (baselineError) {
            return <Alert type="error" message={baselineError.message} />;
        }
        if (baselineLoading || !baseline) {
            return <Spin />;
        }

        const creditMaxHint = tr.ledger.creditMax({amount: renderMoney(maxCredit)});

        return (
            <div>
                {canSwitchPaymentType && paymentTypeSwitch && (
                    <div style={{marginBottom: 16}}>
                        <div style={{marginBottom: 4}}>{tr.paymentTypeLabel()}</div>
                        <Radio.Group value={newPaymentType} onChange={(e) => setNewPaymentType(e.target.value)}>
                            <Radio value={PaymentType.PrePaid} disabled={!paymentTypeSwitch.allowUpFront && paymentTypeSwitch.current !== PaymentType.PrePaid}>
                                {tr.paymentTypes.PrePaid()}
                            </Radio>
                            <Radio value={PaymentType.Monthly} disabled={!paymentTypeSwitch.allowMonthly && paymentTypeSwitch.current !== PaymentType.Monthly}>
                                {tr.paymentTypes.Monthly()}
                            </Radio>
                        </Radio.Group>
                        {paymentTypeChanged && <Alert type="info" showIcon message={tr.paymentTypeHint()} style={{marginTop: 8}} />}
                    </div>
                )}

                <table style={{width: "100%", borderCollapse: "collapse"}}>
                    <tbody>
                        {renderLedgerRow(
                            tr.ledger.coursePrice(),
                            renderMoney(currentBasePrice),
                            <MoneyInput value={newBasePrice} onChange={(v) => setNewBasePrice(v ?? 0)} />,
                        )}
                        {discount > 0 &&
                            renderLedgerRow(discountName ?? tr.ledger.discount(), `− ${renderMoney(discount)}`, `− ${renderMoney(discount)}`, {muted: true})}
                        {memberDiscount > 0 &&
                            renderLedgerRow(tr.ledger.memberDiscount(), `− ${renderMoney(memberDiscount)}`, `− ${renderMoney(memberDiscount)}`, {muted: true})}
                        {maxCredit > 0 &&
                            renderLedgerRow(
                                <span>
                                    {tr.ledger.creditedBalance()} <span style={{color: "rgba(0,0,0,.45)"}}>{creditMaxHint}</span>
                                </span>,
                                `− ${renderMoney(currentCreditedBalance)}`,
                                <MoneyInput value={creditedBalance} onChange={(v) => setCreditedBalance(Math.max(0, Math.min(v ?? 0, maxCredit)))} />,
                            )}
                        {renderLedgerRow(tr.ledger.grossPrice(), renderMoney(expectedGrossPrice), renderMoney(liveGross), {sum: true})}
                        {renderLedgerRow(tr.ledger.alreadyPaid(), renderMoney(alreadyPaid), renderMoney(alreadyPaid))}
                        {renderLedgerRow(
                            tr.ledger.openAmount(),
                            renderMoney(currentOpen),
                            <span>
                                {renderMoney(liveOverpayment > 0 ? 0 : liveOpen)}
                                {renderOpenAmountTag()}
                            </span>,
                        )}
                    </tbody>
                </table>

                {liveOverpayment > 0 && (
                    <div style={{marginTop: 16}}>
                        <div style={{marginBottom: 4}}>{tr.overpaymentQuestion({amount: renderMoney(liveOverpayment)})}</div>
                        <Radio.Group value={refundMode} onChange={(e) => setRefundMode(e.target.value)}>
                            <Radio value={CorrectionRefundMode.BookerCredit} disabled={creditIncreased}>
                                {tr.refundCredit()}
                            </Radio>
                            <Radio value={CorrectionRefundMode.MollieRefund}>{tr.refundMollie()}</Radio>
                        </Radio.Group>
                    </div>
                )}

                <div style={{marginTop: 20}}>
                    <div style={{marginBottom: 4}}>
                        {tr.reasonLabel()} <span style={{color: "rgba(0,0,0,.45)"}}>{tr.reasonHint()}</span>
                    </div>
                    <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        maxLength={REASON_MAX_LENGTH}
                        placeholder={tr.reasonPlaceholder()}
                    />
                </div>

                {paymentTypeSwitch && !canSwitchPaymentType && (
                    <p style={{color: "rgba(0,0,0,.45)", marginTop: 12, marginBottom: 0}}>
                        {tr.paymentTypeFixed({paymentType: currentPaymentTypeLabel})}
                    </p>
                )}

            </div>
        );
    };

    // Vorschau und Ergebnis zeigen dieselbe Liste: Schritt 2 im Futur, Schritt 3 im Perfekt.
    // Grundlage ist in beiden Fällen der serverseitig geplante Vorschau-Datensatz, den der
    // Executor unverändert nachvollzieht.
    const renderSentences = (done: boolean) => {
        if (!preview) {
            return null;
        }
        const sentences = buildCorrectionSentences({
            preview,
            oldGrossPrice: done ? grossPriceBeforeExec ?? expectedGrossPrice : expectedGrossPrice,
            done,
            outcome: done && execResult ? execResult : undefined,
            rates,
            reason,
            renderMoney,
        });

        return (
            <ul style={{listStyle: "none", margin: 0, padding: 0}}>
                {sentences.map((sentence) => (
                    <li key={sentence.key} style={{display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #f0f0f0"}}>
                        <span style={{width: 20, flex: "0 0 20px", textAlign: "center", color: done ? green[6] : blue[5]}}>
                            {done ? "✓" : "•"}
                        </span>
                        <span>
                            {sentence.text}
                            {sentence.hint && <div style={{color: "rgba(0,0,0,.45)", fontSize: 13}}>{sentence.hint}</div>}
                        </span>
                    </li>
                ))}
            </ul>
        );
    };

    const renderPreviewStep = () => {
        if (previewError) {
            return <Alert type="error" message={previewError.message} />;
        }
        if (previewLoading || !preview) {
            return <Spin />;
        }

        return (
            <div>
                <table style={{width: "100%", borderCollapse: "collapse", marginBottom: 16}}>
                    <tbody>
                        {renderLedgerRow(tr.ledger.grossPrice(), renderMoney(expectedGrossPrice), renderMoney(preview.newGrossPrice), {sum: true})}
                        {renderLedgerRow(tr.ledger.alreadyPaid(), renderMoney(alreadyPaid), renderMoney(preview.alreadyPaid))}
                        {renderLedgerRow(tr.ledger.openAmount(), renderMoney(currentOpen), renderMoney(preview.newOpenAmount))}
                    </tbody>
                </table>

                <b>{tr.whatHappensHeader()}</b>
                <div style={{marginTop: 8}}>{renderSentences(false)}</div>

                <div style={{marginLeft: 30, marginTop: 12}}>
                    <Checkbox checked={sendMail} onChange={(e) => setSendMail(e.target.checked)}>
                        {tr.sendMail()}
                    </Checkbox>
                </div>

                <p style={{color: "rgba(0,0,0,.45)", fontSize: 13, marginTop: 12, marginBottom: 0}}>{tr.notAffected()}</p>

                {preview.warnings.map((w, i) => (
                    <Alert key={`w-${i}`} type="warning" message={w} style={{marginTop: 8}} />
                ))}
                {preview.blockers.map((b, i) => (
                    <Alert key={`b-${i}`} type="error" message={b} style={{marginTop: 8}} />
                ))}
            </div>
        );
    };

    const renderResultStep = () => {
        const messages = execResult?.messages ?? [];

        return (
            <Result status="success" title={tr.resultTitle({amount: renderMoney(preview?.newGrossPrice ?? liveGross)})}>
                {renderSentences(true)}
                {messages.map((m, i) => (
                    <Alert key={`m-${i}`} type="warning" message={m} style={{marginTop: 8}} />
                ))}
            </Result>
        );
    };

    const footer = () => {
        if (step === 0) {
            return [
                <Button key="cancel" onClick={close}>{tr.buttons.cancel()}</Button>,
                <Button
                    key="next"
                    type="primary"
                    disabled={baselineLoading || !baseline || nothingChanged || refundModeMissing}
                    onClick={goToPreview}
                >
                    {tr.buttons.preview()}
                </Button>,
            ];
        }
        if (step === 1) {
            const executeLabel = preview
                ? tr.buttons.execute({amount: renderMoney(preview.newGrossPrice)})
                : tr.buttons.executeUnchanged();

            return [
                <Button key="back" onClick={() => setStep(0)}>{tr.buttons.back()}</Button>,
                <Button key="ok" type="primary" disabled={blockersPresent || previewLoading || Boolean(previewError) || !preview} loading={executing} onClick={runExecute}>
                    {executeLabel}
                </Button>,
            ];
        }

        return [<Button key="done" type="primary" onClick={close}>{tr.buttons.done()}</Button>];
    };

    if (!open) {
        return (
            <Button style={{color: blue[5], borderColor: blue[3]}} size="small" onClick={openWizard}>
                {tr.triggerButton()}
            </Button>
        );
    }

    return (
        <Modal title={tr.modalTitle()} open={open} onCancel={close} footer={footer()} width={640}>
            <Steps current={step} size="small" style={{marginBottom: 16}}>
                <Steps.Step title={tr.steps.input()} />
                <Steps.Step title={tr.steps.preview()} />
                <Steps.Step title={tr.steps.result()} />
            </Steps>
            {step < 2 && <ActiveDunningAlert bookingId={bookingId} variant="edit" />}
            {step === 0 && renderInputStep()}
            {step === 1 && renderPreviewStep()}
            {step === 2 && renderResultStep()}
        </Modal>
    );
};

export default memo(CorrectBookingWizard);
